import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import track from '../../../../../../../shared/util/utilities/mixpanel';
import { Form, Field } from '../../../../../../../shared/components/Form';
import { Button, Group, Dialog } from '@economic/taco';
import { t } from '../../../../../../../shared/components/l10n/l10n';

import { OnboardingStatusCompanyData } from '../../../../../../../forms/GettingStartedSplash/types';
import cvrLookup from '../../../../../../../shared/util/utilities/cvrLookup';

interface Props {
    next: () => void;
    back: () => void;
    companyData: OnboardingStatusCompanyData;
}

const CompanyDataStep = ({ next, back, companyData }: Props) => {
    const navigate = useNavigate();
    const isNextButtonDisabled = !companyData.cvr || !companyData.name;

    const verifyCVR = async (value: string) => {
        const companyInfo = await cvrLookup(value);
        return typeof companyInfo === 'object';
    };

    const handleSubmit = () => {
        track('smartpay', 'signup.modal.company-information-confirmed');
        if (typeof next === 'function') next();
    };

    const initialValues = {
        cvr: companyData.cvr,
        companyName: companyData.name,
    };

    const companyDataValidationSchema = Yup.object({
        cvr: Yup.string()
            .required(t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CVR_REQUIRED_VALIDATION_ERROR`)
            .matches(/^([dD][kK])?(\d{8})$/, t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CVR_VALIDATION_ERROR`)
            .test(
                'verify-cvr',
                t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CVR_INEXISTENT_ERROR`,
                async value => (await verifyCVR(value)) === true
            ),
        companyName: Yup.string().required(),
    });

    return (
        <>
            <Dialog.Title>
                <div className="my-4">{t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_TITLE`}</div>
            </Dialog.Title>

            <Form
                onSubmit={handleSubmit}
                skipNotifications
                initialData={initialValues}
                validationSchema={companyDataValidationSchema}>
                <div className="flex flex-col">
                    <span>{t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_TEXT`}</span>

                    <div className="flex flex-col my-2">
                        <Field.Text label={t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_CVR`} name="cvr" readOnly />
                    </div>

                    <div className="flex flex-col my-2">
                        <Field.Text label={t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_NAME`} name="companyName" readOnly />
                    </div>
                </div>

                <Dialog.Footer className="!justify-between">
                    <Button
                        appearance="discrete"
                        className="px-4"
                        href="/settings/company/master-information"
                        onClick={() => navigate('/settings/company/master-information')}>
                        {t`SMARTPAY_ONBOARDING_MODAL_COMPANY_DATA_MASTER_INFO_BUTTON`}
                    </Button>

                    <Group>
                        <Button onClick={back}>{t`SMARTPAY_ONBOARDING_MODAL_BACK_BUTTON`}</Button>
                        <Button autoFocus appearance="primary" type="submit" disabled={isNextButtonDisabled}>
                            {t`SMARTPAY_ONBOARDING_MODAL_NEXT_BUTTON`}
                        </Button>
                    </Group>
                </Dialog.Footer>
            </Form>
        </>
    );
};

export default CompanyDataStep;
